import { Mail, Phone, MapPin, Clock, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { submitInquiry } from '../lib/cms-api';
import { useCMS, pickLoc } from '../context/CMSContext';
import { useSeo, breadcrumb } from '../lib/seo';

const Contact = () => {
  const { t, i18n } = useTranslation();
  const { cms, lang } = useCMS();

  const waNumber = (cms.site?.whatsappNumber || '').replace(/[^\d]/g, '');
  const waText = i18n.exists('whatsapp.message')
    ? t('whatsapp.message')
    : 'Hello, I would like to enquire about your advisory services.';
  const waHref = waNumber
    ? `https://wa.me/${waNumber}?text=${encodeURIComponent(waText)}`
    : null;

  const seo = cms.seo.pages.contact;

  useSeo({
    title: pickLoc(seo.title, lang),
    description: pickLoc(seo.description, lang),
    keywords: pickLoc(seo.keywords, lang),
    path: '/contact',
    jsonLd: [
      breadcrumb([
        { name: '首頁', path: '/' },
        { name: t('nav.contact'), path: '/contact' },
      ]),
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: cms.site.orgNameEn,
        about: {
          '@type': 'ProfessionalService',
          name: cms.site.orgNameTc,
          alternateName: cms.site.orgNameEn,
        },
      },
    ],
  });

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.id]: e.target.value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      const fullMessage = `Subject: ${formData.subject}\n\n${formData.message}`;

      await submitInquiry({
        name: `${formData.firstName} ${formData.lastName}`.trim(),
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        message: fullMessage,
      });

      setStatus('success');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error(error);
      setStatus('error');
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-navy-900 mb-6 font-serif">{t('contact.title')}</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-8">
            {waHref && (
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-[#25D366] hover:bg-[#1fb857] text-white p-6 rounded-lg shadow-md transition-colors"
              >
                <div className="flex items-center gap-4">
                  <svg viewBox="0 0 32 32" className="h-10 w-10 fill-current flex-shrink-0" aria-hidden="true">
                    <path d="M16.004 3.2c-7.06 0-12.8 5.74-12.8 12.8 0 2.26.6 4.46 1.72 6.4L3.2 28.8l6.56-1.68a12.74 12.74 0 0 0 6.24 1.6h.004c7.06 0 12.8-5.74 12.8-12.8s-5.74-12.8-12.8-12.8zm0 23.47h-.004a10.6 10.6 0 0 1-5.4-1.48l-.39-.23-3.89 1 1.04-3.8-.25-.4a10.56 10.56 0 0 1-1.62-5.63c0-5.87 4.78-10.64 10.65-10.64 2.85 0 5.52 1.11 7.53 3.12a10.58 10.58 0 0 1 3.12 7.53c0 5.87-4.78 10.53-10.65 10.53zm5.84-7.9c-.32-.16-1.93-.95-2.23-1.06-.3-.11-.52-.16-.74.16-.22.32-.85 1.06-1.04 1.28-.19.22-.38.24-.7.08-.32-.16-1.36-.5-2.59-1.6-.96-.85-1.6-1.91-1.79-2.23-.19-.32-.02-.5.14-.66.14-.14.32-.38.48-.56.16-.19.22-.32.32-.54.11-.22.05-.4-.03-.56-.08-.16-.72-1.77-.99-2.42-.26-.63-.53-.55-.72-.56l-.62-.01c-.22 0-.56.08-.85.4-.3.32-1.12 1.1-1.12 2.68s1.15 3.11 1.31 3.32c.16.22 2.24 3.55 5.5 4.84.77.33 1.37.53 1.84.68.81.26 1.55.22 2.13.13.65-.1 2-.82 2.28-1.61.28-.79.28-1.47.2-1.61-.08-.14-.3-.22-.62-.38z" />
                  </svg>
                  <div>
                    <p className="text-lg font-bold">WhatsApp</p>
                    <p className="text-sm opacity-90">{t('contact.whatsappCta')}</p>
                  </div>
                </div>
              </a>
            )}
            <div className="bg-white p-8 rounded-lg shadow-md border-t-4 border-gold-500">
              <h3 className="text-xl font-bold text-navy-900 mb-6 font-serif">{t('contact.title')}</h3>

              <div className="space-y-6">
                <div className="flex items-start">
                  <MapPin className="h-6 w-6 text-gold-600 mt-1" />
                  <div className="ml-4">
                    <h4 className="font-semibold text-navy-900">{t('contact.info.office')}</h4>
                    <p className="text-gray-600">{t('footer.address')}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Phone className="h-6 w-6 text-gold-600 mt-1" />
                  <div className="ml-4">
                    <h4 className="font-semibold text-navy-900">{t('contact.info.phone')}</h4>
                    <p className="text-gray-600">{t('footer.phone')}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Mail className="h-6 w-6 text-gold-600 mt-1" />
                  <div className="ml-4">
                    <h4 className="font-semibold text-navy-900">{t('contact.info.email')}</h4>
                    <p className="text-gray-600">{t('footer.email')}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Globe className="h-6 w-6 text-gold-600 mt-1" />
                  <div className="ml-4">
                    <h4 className="font-semibold text-navy-900">{t('contact.info.website')}</h4>
                    <p className="text-gray-600">{t('footer.website')}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Clock className="h-6 w-6 text-gold-600 mt-1" />
                  <div className="ml-4">
                    <h4 className="font-semibold text-navy-900">{t('contact.info.hours')}</h4>
                    <p className="text-gray-600">{t('contact.info.hoursVal')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="bg-white p-8 rounded-lg shadow-md">
              {status === 'success' ? (
                <div className="text-center py-12">
                  <div className="text-gold-500 text-5xl mb-4">✓</div>
                  <h3 className="text-2xl font-bold text-navy-900 mb-2">{t('contact.form.success')}</h3>
                  <p className="text-gray-600">{t('contact.form.successMessage') || "Thank you for your message. We will get back to you shortly."}</p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="mt-6 px-6 py-2 bg-navy-900 text-white rounded hover:bg-navy-800 transition-colors"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-navy-700 mb-1">{t('contact.form.firstName')}</label>
                      <input
                        type="text"
                        id="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-gold-500 focus:border-gold-500 outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-navy-700 mb-1">{t('contact.form.lastName')}</label>
                      <input
                        type="text"
                        id="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-gold-500 focus:border-gold-500 outline-none transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-navy-700 mb-1">{t('contact.form.email')}</label>
                      <input
                        type="email"
                        id="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-gold-500 focus:border-gold-500 outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-navy-700 mb-1">{t('contact.form.phone')}</label>
                      <input
                        type="tel"
                        id="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-gold-500 focus:border-gold-500 outline-none transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-navy-700 mb-1">{t('contact.form.company') || "Company"}</label>
                    <input
                      type="text"
                      id="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-gold-500 focus:border-gold-500 outline-none transition-colors"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-navy-700 mb-1">{t('contact.form.subject')}</label>
                    <select
                      id="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-gold-500 focus:border-gold-500 outline-none transition-colors"
                    >
                      <option value="">{t('contact.form.selectTopic')}</option>
                      <option value="ipo">{t('contact.form.topics.ipo')}</option>
                      <option value="ma">{t('contact.form.topics.ma')}</option>
                      <option value="corpFin">{t('contact.form.topics.corpFin')}</option>
                      <option value="corpStruct">{t('contact.form.topics.corpStruct')}</option>
                      <option value="opsMgmt">{t('contact.form.topics.opsMgmt')}</option>
                      <option value="other">{t('contact.form.topics.other')}</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-navy-700 mb-1">{t('contact.form.message')}</label>
                    <textarea
                      id="message"
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-gold-500 focus:border-gold-500 outline-none transition-colors"
                    ></textarea>
                  </div>

                  {status === 'error' && (
                    <div className="text-red-600 text-sm">
                      {t('contact.form.error') || "Something went wrong. Please try again."}
                    </div>
                  )}

                  <div>
                    <button
                      type="submit"
                      disabled={status === 'submitting'}
                      className="w-full md:w-auto px-8 py-3 bg-gold-500 hover:bg-gold-600 text-navy-900 font-bold rounded transition-colors disabled:opacity-50"
                    >
                      {status === 'submitting' ? 'Sending...' : t('contact.form.submit')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
